import React, { useState, useRef, useEffect } from "react";
import { ChevronDownIcon, UserIcon, ArrowPathIcon, Cog6ToothIcon, PuzzlePieceIcon } from "@heroicons/react/24/outline";
import { Link, useLocation } from "react-router-dom";
import { useContext } from "react";
import { AuthContext } from "../contexts/AuthContext";
import { canAccessPage } from "../constants/roleConfig";

export default function Layout({ children }) {
    const { user, logout, isAuthenticated, switchRole } = useContext(AuthContext);


    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);
    const location = useLocation();

    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    return (
        <div className="flex h-screen w-screen" style={{ background: 'none' }}>
            <div className="flex-1 flex flex-col min-w-0 h-full">
                <header className="flex items-center justify-between px-6 py-3 bg-gray-100 border-b">
                    <div className="flex items-center gap-8">
                        <div className="font-bold text-lg">ASC Anesthesia Associates Inc</div>
                        {isAuthenticated && (
                            <nav className="flex items-center gap-4">
                                {canAccessPage(user?.activeRole, '/schedule') && (
                                    <Link
                                        to="/schedule"
                                        className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${location.pathname === '/schedule'
                                                ? 'bg-blue-100 text-blue-700'
                                                : 'text-gray-700 hover:bg-gray-200'
                                            }`}
                                    >
                                        Schedule
                                    </Link>
                                )}
                                {canAccessPage(user?.activeRole, '/calendar') && (
                                    <Link
                                        to="/calendar"
                                        className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${location.pathname === '/calendar'
                                                ? 'bg-blue-100 text-blue-700'
                                                : 'text-gray-700 hover:bg-gray-200'
                                            }`}
                                    >
                                        Calendar
                                    </Link>
                                )}
                                {canAccessPage(user?.activeRole, '/facilities') && (
                                    <Link
                                        to="/facilities"
                                        className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${location.pathname === '/facilities'
                                                ? 'bg-blue-100 text-blue-700'
                                                : 'text-gray-700 hover:bg-gray-200'
                                            }`}
                                    >
                                        Facilities
                                    </Link>
                                )}
                                {canAccessPage(user?.activeRole, '/users') && (
                                    <Link
                                        to="/users"
                                        className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${location.pathname === '/users'
                                                ? 'bg-blue-100 text-blue-700'
                                                : 'text-gray-700 hover:bg-gray-200'
                                            }`}
                                    >
                                        Users
                                    </Link>
                                )}
                                {canAccessPage(user?.activeRole, '/agent-files') && (
                                    <Link
                                        to="/agent-files"
                                        className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${location.pathname === '/agent-files'
                                                ? 'bg-blue-100 text-blue-700'
                                                : 'text-gray-700 hover:bg-gray-200'
                                            }`}
                                    >
                                        Agent Files
                                    </Link>
                                )}
                                {canAccessPage(user?.activeRole, '/assignments') && (
                                    <Link
                                        to="/assignments"
                                        className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${location.pathname === '/assignments'
                                                ? 'bg-blue-100 text-blue-700'
                                                : 'text-gray-700 hover:bg-gray-200'
                                            }`}
                                    >
                                        Assignments
                                    </Link>
                                )}
                                {canAccessPage(user?.activeRole, '/shifts') && (
                                    <Link
                                        to="/shifts"
                                        className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${location.pathname === '/shifts'
                                                ? 'bg-blue-100 text-blue-700'
                                                : 'text-gray-700 hover:bg-gray-200'
                                            }`}
                                    >
                                        Shifts
                                    </Link>
                                )}
                            </nav>
                        )}
                    </div>
                    {isAuthenticated && user && (
                        <div className="relative" ref={dropdownRef}>
                            <button
                                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                className="flex items-center gap-2 px-3 py-2 text-gray-700 hover:bg-gray-200 rounded-md transition-colors"
                            >
                                <UserIcon className="h-5 w-5" />
                                <span className="text-sm font-medium">{user.name || user.email}</span>
                                <ChevronDownIcon className={`h-4 w-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                            </button>

                            {isDropdownOpen && (
                                <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg border border-gray-200 py-1 z-50">
                                    <div className="px-4 py-2 border-b border-gray-100">
                                        <div className="text-sm font-medium text-gray-900">{user.name || user.email}</div>
                                        <div className="text-xs text-gray-500">
                                            Current Role: {user.activeRole || 'Provider'}
                                        </div>
                                    </div>
                                    <Link
                                        to="/profile"
                                        onClick={() => setIsDropdownOpen(false)}
                                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
                                    >
                                        <Cog6ToothIcon className="h-4 w-4" />
                                        Profile Settings
                                    </Link>
                                    <Link
                                        to="/settings"
                                        onClick={() => setIsDropdownOpen(false)}
                                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
                                    >
                                        <PuzzlePieceIcon className="h-4 w-4" />
                                        Claude Plugin
                                    </Link>
                                    {user?.role && user.role.includes(',') && (
                                        <>
                                            {user.activeRole === 'Manager' && (
                                                <>
                                                    {user.role.includes('Provider') && (
                                                        <button
                                                            onClick={async () => {
                                                                await switchRole('Provider');
                                                                setIsDropdownOpen(false);
                                                            }}
                                                            className="w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 transition-colors flex items-center gap-2"
                                                        >
                                                            <ArrowPathIcon className="h-4 w-4" />
                                                            Switch to Provider
                                                        </button>
                                                    )}
                                                    {user.role.includes('Observer') && (
                                                        <button
                                                            onClick={async () => {
                                                                await switchRole('Observer');
                                                                setIsDropdownOpen(false);
                                                            }}
                                                            className="w-full text-left px-4 py-2 text-sm text-yellow-600 hover:bg-yellow-50 transition-colors flex items-center gap-2"
                                                        >
                                                            <ArrowPathIcon className="h-4 w-4" />
                                                            Switch to Observer
                                                        </button>
                                                    )}
                                                </>
                                            )}
                                            {user.activeRole === 'Provider' && (
                                                <>
                                                    {user.role.includes('Manager') && (
                                                        <button
                                                            onClick={async () => {
                                                                await switchRole('Manager');
                                                                setIsDropdownOpen(false);
                                                            }}
                                                            className="w-full text-left px-4 py-2 text-sm text-purple-600 hover:bg-purple-50 transition-colors flex items-center gap-2"
                                                        >
                                                            <ArrowPathIcon className="h-4 w-4" />
                                                            Switch to Manager
                                                        </button>
                                                    )}
                                                    {user.role.includes('Observer') && (
                                                        <button
                                                            onClick={async () => {
                                                                await switchRole('Observer');
                                                                setIsDropdownOpen(false);
                                                            }}
                                                            className="w-full text-left px-4 py-2 text-sm text-yellow-600 hover:bg-yellow-50 transition-colors flex items-center gap-2"
                                                        >
                                                            <ArrowPathIcon className="h-4 w-4" />
                                                            Switch to Observer
                                                        </button>
                                                    )}
                                                </>
                                            )}
                                            {user.activeRole === 'Observer' && (
                                                <>
                                                    {user.role.includes('Manager') && (
                                                        <button
                                                            onClick={async () => {
                                                                await switchRole('Manager');
                                                                setIsDropdownOpen(false);
                                                            }}
                                                            className="w-full text-left px-4 py-2 text-sm text-purple-600 hover:bg-purple-50 transition-colors flex items-center gap-2"
                                                        >
                                                            <ArrowPathIcon className="h-4 w-4" />
                                                            Switch to Manager
                                                        </button>
                                                    )}
                                                    {user.role.includes('Provider') && (
                                                        <button
                                                            onClick={async () => {
                                                                await switchRole('Provider');
                                                                setIsDropdownOpen(false);
                                                            }}
                                                            className="w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 transition-colors flex items-center gap-2"
                                                        >
                                                            <ArrowPathIcon className="h-4 w-4" />
                                                            Switch to Provider
                                                        </button>
                                                    )}
                                                </>
                                            )}
                                        </>
                                    )}
                                    <button
                                        onClick={() => {
                                            logout();
                                            setIsDropdownOpen(false);
                                        }}
                                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                                    >
                                        Logout
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </header>
                <main className="flex-1 min-h-0 overflow-auto p-0">{children}</main>
            </div>
        </div>
    );
} 